import React from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  Dumbbell,
  ArrowUpRight,
  Wind,
  ShieldCheck,
  Repeat,
  CornerUpRight,
  HeartPulse,
  Brain,
  Target,
  Trophy,
  Users,
  Flame,
  MessageSquare,
  ArrowRight,
} from 'lucide-react';
import { SectionHeading, FeatureCard, ImageBlock } from '@/components/Pieces';
import ContactForm from '@/components/ContactForm';
import FAQ from '@/components/FAQ';
import usePageMeta from '@/hooks/usePageMeta';
import { img } from '@/lib/site';

const ATHLETIC = [
  { icon: Zap, title: 'Speed & Agility', desc: 'Acceleration mechanics, change of direction, deceleration control.' },
  { icon: Dumbbell, title: 'Strength & Power', desc: 'Force production protocols built around the athlete profile.' },
  { icon: ArrowUpRight, title: 'Vertical Development', desc: 'Plyometric progression and contact-time optimization.' },
  { icon: Wind, title: 'Acceleration Mechanics', desc: 'First-step, sprint posture, and stride frequency systems.' },
  { icon: ShieldCheck, title: 'Injury Prevention', desc: 'Asymmetry screening and load management programming.' },
  { icon: HeartPulse, title: 'Recovery Systems', desc: 'HRV, sleep, soft tissue, breathwork integration.' },
  { icon: CornerUpRight, title: 'Position-Specific Development', desc: 'Programming tailored to position-level demands.' },
  { icon: Repeat, title: 'Return To Play', desc: 'Structured re-entry from injury through performance readiness.' },
];

const MENTAL = [
  { icon: Target, title: 'Focus Development', desc: 'Attention control and pre-performance routines.' },
  { icon: Flame, title: 'Confidence Building', desc: 'Self-efficacy and competition exposure systems.' },
  { icon: Trophy, title: 'Competitive Mindset', desc: 'Compete framework for high-stakes environments.' },
  { icon: Users, title: 'Leadership Development', desc: 'Captain pathways and accountability infrastructure.' },
  { icon: Brain, title: 'Emotional Discipline', desc: 'Regulation under fatigue, scoreboard pressure, adversity.' },
  { icon: ShieldCheck, title: 'Pressure Management', desc: 'Performance under load — physical, mental, organizational.' },
  { icon: HeartPulse, title: 'Burnout Prevention', desc: 'Workload, recovery, and identity periodization.' },
  { icon: MessageSquare, title: 'Team Communication', desc: 'On-field, in-room, and async communication systems.' },
];

const CROSSOVER = [
  'Resilience',
  'Communication',
  'Leadership',
  'Emotional Control',
  'Recovery',
  'Discipline',
  'Pressure Management',
];

export default function SportsPerformancePage() {
  usePageMeta({
    title: 'Sports Performance & Mental Resilience Training | Ball Raw Performance',
    description:
      'Athletic performance and mental resilience training for athletes — plus corporate resilience training that helps employees, leaders, and teams build confidence, focus, and workplace performance under pressure.',
    path: '/sports-performance',
  });
  return (
    <div data-testid="page-sports-performance">
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={img('sports_speed')} alt="Sprinter" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <p className="brp-overline brp-reveal">Sports Performance & Mental Resilience Training</p>
          <h1 className="brp-reveal brp-delay-1 font-display uppercase font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.92] mt-6 max-w-4xl">
            Stronger Athletes. <span className="text-[#D4AF37]">Stronger Minds.</span>
          </h1>
          <p className="brp-reveal brp-delay-2 mt-8 text-lg md:text-xl text-gray-300 max-w-2xl">
            Two distinct tracks under one performance system. Athletic performance and mental resilience training for athletes — and corporate resilience training that helps employees, leaders, and teams perform under workplace pressure.
          </p>
          <div className="brp-reveal brp-delay-3 mt-10 flex flex-wrap gap-4">
            <Link to="/partnerships#contact" className="btn-brp-primary" data-testid="sports-cta-intake">
              Start Athlete Intake <ArrowRight size={16} />
            </Link>
            <Link to="#resilience" className="btn-brp-outline-light" data-testid="sports-cta-resilience">
              Mental Resilience Training
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Track 1 · Athlete Performance"
            title="The Physical Stack For Athletes."
            subtitle="Speed, strength, vertical, mechanics, injury reduction, recovery, position-specific development, and return-to-play. Built for individual athletes, teams, and sports organizations."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ATHLETIC.map((a, i) => (
              <FeatureCard key={a.title} icon={a.icon} title={a.title} desc={a.desc} accent testId={`athletic-${i}`} />
            ))}
          </div>
        </div>
      </section>

      <section id="resilience" className="py-20 md:py-28 px-6 md:px-10 bg-[#0A0A0A] text-white scroll-mt-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <SectionHeading
                light
                overline="Track 2 · Mental Resilience"
                title="Performance Psychology, Not Therapy."
                subtitle="Leadership, focus, and pressure management systems engineered for athletes — and translatable to the workforce. We protect identity and frame this work as performance psychology, not therapy."
              />
            </div>
            <div className="lg:col-span-7">
              <ImageBlock src={img('mental_resilience')} alt="Mental resilience" ratio="aspect-[16/10]" caption="Performance Psychology" />
            </div>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {MENTAL.map((m, i) => (
              <FeatureCard key={m.title} icon={m.icon} title={m.title} desc={m.desc} dark testId={`mental-${i}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            overline="Corporate Crossover"
            title="How Performance Psychology Becomes Workforce Performance."
            subtitle="The same resilience, communication, leadership, and emotional control systems that move athletes through pressure also improve workplace performance — boosting leadership development, team communication, burnout reduction, and employee wellbeing."
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#E5E5E5]">
                {CROSSOVER.map((c) => (
                  <div key={c} className="bg-white p-6 text-center hover:bg-[#0A0A0A] hover:text-white transition group cursor-default">
                    <div className="text-[#D4AF37] font-display text-3xl font-bold group-hover:text-[#D4AF37]">
                      →
                    </div>
                    <div className="mt-3 font-display uppercase tracking-wide text-sm">{c}</div>
                  </div>
                ))}
                <div className="bg-[#0A0A0A] text-[#D4AF37] p-6 text-center">
                  <div className="font-display text-xs uppercase tracking-[0.2em]">All Roads To</div>
                  <div className="mt-3 font-display uppercase tracking-wide text-sm">Workforce Performance</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#555] leading-relaxed">
                Mental health is framed exclusively as <span className="text-[#0A0A0A] font-semibold">Mental Resilience</span>,
                <span className="text-[#0A0A0A] font-semibold"> Performance Psychology</span>, and
                <span className="text-[#0A0A0A] font-semibold"> Pressure Management Systems</span>.
                We protect athlete and employee identity while delivering measurable change.
              </p>
              <Link to="/workforce-systems" className="btn-brp-outline-dark mt-8" data-testid="sports-cta-workforce">
                See Workforce Application
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <FAQ
            testIdPrefix="sports-faq"
            overline="Frequently Asked"
            title="Questions Athletes, Families, And Employers Ask First."
            items={[
              {
                q: 'Is this for individual athletes, teams, or corporate workforces?',
                a: 'All three. We run two distinct tracks — Athletic Performance for athletes, teams, and sports organizations, and Mental Resilience training that applies to athletes and translates directly to leadership and corporate teams.',
              },
              {
                q: 'How does mental resilience training work?',
                a: 'It is performance psychology, not therapy. We build focus, confidence, pressure management, communication, and emotional discipline through structured systems used by elite athletes — protecting identity while delivering measurable change.',
              },
              {
                q: 'Do you offer injury prevention and return-to-play programming?',
                a: 'Yes. Asymmetry screening, load management, soft tissue, and a structured re-entry pathway from injury through full performance readiness.',
              },
              {
                q: 'How does this benefit a corporate workforce?',
                a: 'Resilience, communication, leadership, recovery, and pressure management transfer directly to workplace performance — improving leadership development, team communication, burnout reduction, and employee wellbeing.',
              },
              {
                q: 'Can sports organizations partner with Ball Raw Performance?',
                a: 'Yes. We work with schools, clubs, and sports organizations as a long-term performance partner across athletic development, mental resilience, and recovery infrastructure.',
              },
            ]}
          />
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 bg-[#F9F9F9]" id="contact">
        <div className="max-w-[900px] mx-auto">
          <ContactForm
            formType="programs"
            variant="dark"
            title="Speak With A Performance Specialist"
            subtitle="Athletes, families, sports organizations — tell us what you're building."
            showRole
            showInterest
            ctaLabel="Request Intake"
            testIdPrefix="sports-contact"
          />
        </div>
      </section>
    </div>
  );
}
